/**
 * Liga a pilha de camadas GOES (`useGoesStore`) ao mapa: uma `FrameImageLayer`
 * por camada, atualizada imperativamente (sem React) a cada quadro.
 *
 * Regras:
 * - a imagem exibida só é trocada quando a nova já está decodificada; enquanto
 *   não está, a anterior continua na tela (nunca pisca);
 * - se a imagem daquele horário não existe (lacuna) ou falhou de vez, a camada
 *   fica ESCONDIDA nesse quadro — nunca mostra um quadro velho com o horário novo;
 * - cada imagem usa a extensão exata do seu produto/tamanho (georef) e, por
 *   padrão, a faixa de rodapé da NOAA é recortada (`layer.setExtent`);
 * - com o servidor de blocos no ar, cada camada tem logo acima o DETALHE
 *   (detalhe/camada.ts): os blocos à vista, no nível que a tela pede, do mesmo
 *   quadro que a imagem inteira mostra — nunca de outro horário;
 * - além do zoom máximo do GOES a imagem vai ficando transparente e revela o
 *   foto de fundo por baixo (`goesFade` multiplica a opacidade escolhida, que
 *   fica intacta na pilha e no link); no fim da transição deixa de ser
 *   desenhada (`goesMinResolution`) — sem mexer em `visible`, que diz se o
 *   quadro está pronto.
 */
import type OlMap from 'ol/Map';
import { getProductName } from '../config/products';
import { frameCache, type FrameCache } from '../goes/frameCache';
import { STAR_SECTOR_PAGE, type ImageWidth } from '../goes/star';
import { subscribeSelector } from '../lib/store';
import { activePlanLayers, currentPlayWidth, layerUrlAt, usePlanStore, widthFromUrl, type LayerFrameStatus } from '../loop/plan';
import { useLoopStore } from '../loop/store';
import { useDisplayStore } from '../state/display';
import { useGoesStore } from '../state/goes';
import { useTimeStore } from '../state/time';
import { DetalheLayer } from '../detalhe/camada';
import { useDetalheStore } from '../detalhe/estado';
import { nivelDoProduto } from '../detalhe/blocos';
import { niveisDo, useBlocosStore } from '../detalhe/saude';
import { FrameImageLayer, FrameSource } from './FrameLayer';
import { goesFade, goesMinResolution, useFundoStore } from './fundoLayer';
import { footerClipExtent, imageExtentFor } from './imageGeometry';
import { NSA_EXTENT } from './projection';
import { Z } from './zIndex';

/**
 * Créditos sempre visíveis. Os focos (INPE) trazem o próprio crédito na
 * camada deles; os limites vetoriais não, então o crédito vai aqui.
 */
export const GOES_ATTRIBUTIONS = [
  `Imagens GOES-19: <a href="${STAR_SECTOR_PAGE}" target="_blank" rel="noopener">NOAA/NESDIS/STAR</a>`,
  'Limites: IBGE · FUNAI · MMA · DNIT',
];

interface Slot {
  uid: string;
  layer: FrameImageLayer;
  source: FrameSource;
  /** Blocos à vista, no nível pedido, do quadro que `layer` mostra. */
  detalhe: DetalheLayer;
  shownUrl: string | null;
  shownTime: number | null;
  shownProduct: string | null;
  shownWidth: ImageWidth | null;
}

export class GoesLayerManager {
  private readonly slots = new Map<string, Slot>();
  private readonly unsubs: Array<() => void> = [];
  private readonly waiters = new Set<() => void>();
  private disposed = false;

  constructor(
    private readonly map: OlMap,
    private readonly cache: FrameCache = frameCache,
  ) {
    const refresh = () => this.refresh();
    const applyFade = () => this.applyOpacities();
    const view = map.getView();
    view.on('change:resolution', applyFade);
    this.unsubs.push(
      () => view.un('change:resolution', applyFade),
      useGoesStore.subscribe(() => {
        this.syncLayers();
        this.refresh();
      }),
      subscribeSelector(usePlanStore, (s) => s.plan, refresh),
      subscribeSelector(usePlanStore, (s) => s.stillWidth, refresh),
      subscribeSelector(useTimeStore, (s) => s.index, refresh),
      subscribeSelector(useTimeStore, (s) => s.frames, refresh),
      subscribeSelector(useLoopStore, (s) => s.playing, refresh),
      subscribeSelector(useDisplayStore, (s) => s.showNoaaFooter, () => this.applyClips()),
      subscribeSelector(useFundoStore, (s) => s.falhou, () => this.syncLayers()),
      subscribeSelector(useDetalheStore, (s) => s.nivel, refresh),
      subscribeSelector(useBlocosStore, (s) => s.niveis, refresh),
      cache.subscribe(refresh),
    );
    this.syncLayers();
    this.refresh();
  }

  /** Cria/remove/reordena as camadas do OL conforme a pilha. */
  private syncLayers(): void {
    const layers = useGoesStore.getState().layers;
    const uids = new Set(layers.map((l) => l.uid));
    for (const [uid, slot] of this.slots) {
      if (!uids.has(uid)) {
        this.map.removeLayer(slot.layer);
        this.map.removeLayer(slot.detalhe);
        slot.detalhe.dispose();
        if (slot.shownUrl) this.cache.unpin(slot.shownUrl);
        slot.source.setFrameImage(null);
        this.slots.delete(uid);
      }
    }
    layers.forEach((l, i) => {
      let slot = this.slots.get(l.uid);
      if (!slot) {
        const source = new FrameSource(NSA_EXTENT, GOES_ATTRIBUTIONS);
        const layer = new FrameImageLayer({ source, visible: false, className: 'ol-layer goes-layer' });
        layer.set('goes', true);
        layer.set('goesUid', l.uid);
        this.map.addLayer(layer);
        const detalhe = new DetalheLayer('ol-layer detalhe-layer');
        detalhe.setVisible(false);
        this.map.addLayer(detalhe);
        slot = { uid: l.uid, layer, source, detalhe, shownUrl: null, shownTime: null, shownProduct: null, shownWidth: null };
        this.slots.set(l.uid, slot);
      }
      slot.layer.setZIndex(Z.GOES_BASE + i);
      // Logo acima da própria base, abaixo da próxima camada GOES da pilha.
      slot.detalhe.setZIndex(Z.GOES_BASE + i + 0.5);
      slot.layer.setMinResolution(goesMinResolution());
      slot.detalhe.setMinResolution(goesMinResolution());
      slot.layer.set('title', getProductName(l.productId));
    });
    this.applyOpacities();
  }

  /** Opacidade escolhida × transição para a foto de fundo conforme o zoom. */
  private applyOpacities(): void {
    const fade = goesFade(this.map.getView().getResolution());
    for (const l of useGoesStore.getState().layers) {
      const slot = this.slots.get(l.uid);
      slot?.layer.setOpacity(l.opacity * fade);
      slot?.detalhe.setOpacity(l.opacity * fade);
    }
  }

  /** Recorte do rodapé da NOAA conforme a preferência e o tamanho exibido (base e detalhe). */
  private applyClip(slot: Slot): void {
    const showFooter = useDisplayStore.getState().showNoaaFooter;
    if (slot.shownProduct && slot.shownWidth) {
      const extent = imageExtentFor(slot.shownProduct, slot.shownWidth);
      slot.layer.setExtent(showFooter ? undefined : footerClipExtent(extent, slot.shownWidth));
    }
    const q = slot.detalhe.getQuadro();
    if (q) slot.detalhe.setExtent(showFooter ? undefined : footerClipExtent(imageExtentFor(q.produto, q.nivel), q.nivel));
  }

  /**
   * Detalhe de cada camada: o quadro que a base mostra AGORA (produto e
   * instante), no nível que o motor escolheu (o do produto: 14400 só onde
   * existe). Base escondida (lacuna, falha,
   * camada oculta) = detalhe escondido.
   */
  private syncDetalhe(): void {
    const { nivel } = useDetalheStore.getState();
    for (const slot of this.slots.values()) {
      const q =
        nivel && slot.layer.getVisible() && slot.shownProduct && slot.shownTime !== null
          ? { produto: slot.shownProduct, tempo: slot.shownTime, nivel: nivelDoProduto(nivel, niveisDo(slot.shownProduct)) }
          : null;
      slot.detalhe.setQuadro(q);
      slot.detalhe.setVisible(q !== null);
      if (q) this.applyClip(slot);
    }
  }

  private applyClips(): void {
    for (const slot of this.slots.values()) this.applyClip(slot);
  }

  /** Mostra, em cada camada, a melhor imagem pronta do quadro atual. */
  private refresh(): void {
    if (this.disposed) return;
    const { plan, stillWidth } = usePlanStore.getState();
    const { index } = useTimeStore.getState();
    const { playing, exportLocked } = useLoopStore.getState();
    // Tocando (ou exportando): só a imagem decodificada na largura em que a
    // animação toca — nada de substitutos de outra largura (sem piscar nitidez).
    const strict = playing || exportLocked;
    const playWidth = currentPlayWidth();
    const status: Record<string, LayerFrameStatus> = {};
    let shownAny = false;

    for (const l of useGoesStore.getState().layers) {
      const slot = this.slots.get(l.uid);
      if (!slot) continue;
      if (!l.visible) {
        slot.layer.setVisible(false);
        continue;
      }
      const pl = plan?.layers.find((p) => p.uid === l.uid);
      if (!plan || !pl || pl.pending || pl.productId !== l.productId) {
        // Listagem ainda não chegou: mantém o que já está na tela.
        slot.layer.setVisible(slot.shownUrl !== null);
        status[l.uid] = 'loading';
        continue;
      }
      const t = pl.times[index];
      if (t === null || t === undefined) {
        // Sem imagem desta camada perto deste horário: esconde.
        slot.layer.setVisible(false);
        status[l.uid] = 'none';
        continue;
      }
      const exactUrl = playWidth ? layerUrlAt(pl, index, playWidth) : null;
      const best = strict
        ? exactUrl && this.cache.get(exactUrl)
          ? { url: exactUrl, image: this.cache.get(exactUrl)!, width: widthFromUrl(exactUrl) }
          : null
        : this.cache.bestReady(pl.productId, t, stillWidth ?? plan.width);
      if (best) {
        // Mesma URL com bitmap novo (redecodificada noutra largura): só troca a imagem.
        if (best.url !== slot.shownUrl || best.image !== slot.source.getFrameImage()) {
          if (best.url !== slot.shownUrl) {
            this.cache.pin(best.url);
            if (slot.shownUrl) this.cache.unpin(slot.shownUrl);
          }
          slot.source.setFrameImage(best.image, imageExtentFor(pl.productId, best.width));
          slot.shownUrl = best.url;
          slot.shownTime = t;
          slot.shownProduct = pl.productId;
          slot.shownWidth = best.width;
          this.applyClip(slot);
        }
        slot.layer.setVisible(true);
        status[l.uid] = 'ok';
        shownAny = true;
        continue;
      }
      const url = exactUrl ?? pl.urls[index];
      if (url && this.cache.status(url) === 'error') {
        // Imagem indisponível neste horário: esconde em vez de mostrar a anterior.
        slot.layer.setVisible(false);
        status[l.uid] = 'failed';
        continue;
      }
      // Ainda baixando: a anterior continua (sem piscar).
      slot.layer.setVisible(slot.shownUrl !== null);
      status[l.uid] = 'loading';
      if (slot.shownUrl) shownAny = true;
    }

    this.syncDetalhe();

    // Instante da imagem que está DE FATO na tela (camada de base visível): os
    // focos seguem este valor e nunca ficam à frente/atrás da imagem enquanto
    // o quadro pedido ainda decodifica (ver `displayedTime` em state/time.ts).
    let displayed: number | null = null;
    for (const l of useGoesStore.getState().layers) {
      const slot = this.slots.get(l.uid);
      if (slot && l.visible && slot.layer.getVisible() && slot.shownTime !== null) {
        displayed = slot.shownTime;
        break;
      }
    }
    useTimeStore.getState().setDisplayedTime(displayed);

    this.publishStatus(status);
    // Nenhuma camada visível tem imagem neste quadro (ex.: a NOAA parou a base
    // e a linha do tempo segue sem ela, loop/semNoaa.ts): não há o que esperar,
    // e o mapa já está pronto com as outras camadas.
    // Uma camada que falhou junto (404 no horário exato) também não vai chegar;
    // só a falha da base fica de fora — ela faz a abertura recuar um quadro (startup.ts).
    const values = Object.values(status);
    const baseUid = useGoesStore.getState().layers[0]?.uid;
    const nadaAEsperar =
      values.some((v) => v === 'none') && values.every((v) => v === 'none' || v === 'failed') && (!baseUid || status[baseUid] !== 'failed');
    if ((shownAny || nadaAEsperar) && !usePlanStore.getState().firstFrameShown) {
      usePlanStore.setState({ firstFrameShown: true });
      this.map.once('rendercomplete', () => {
        document.documentElement.dataset.goesReady = '1';
      });
    }
    if (this.waiters.size && this.isCurrentFrameReady()) {
      for (const w of [...this.waiters]) w();
    }
  }

  private publishStatus(next: Record<string, LayerFrameStatus>): void {
    const prev = usePlanStore.getState().frameStatus;
    const keys = Object.keys(next);
    if (keys.length === Object.keys(prev).length && keys.every((k) => prev[k] === next[k])) return;
    usePlanStore.setState({ frameStatus: next });
  }

  /**
   * O quadro atual está corretamente na tela em todas as camadas visíveis?
   * (imagem daquele horário exibida, ou camada escondida por lacuna/falha)
   */
  isCurrentFrameReady(): boolean {
    const { plan } = usePlanStore.getState();
    if (!plan) return false;
    const { index } = useTimeStore.getState();
    // Exportando: exige a largura em que a animação toca (vídeo com nitidez uniforme).
    const strict = useLoopStore.getState().exportLocked;
    const playWidth = currentPlayWidth();
    for (const pl of activePlanLayers(plan)) {
      const t = pl.times[index];
      if (t === null || t === undefined) continue;
      const slot = this.slots.get(pl.uid);
      if (!slot) continue;
      const exactUrl = playWidth ? layerUrlAt(pl, index, playWidth) : null;
      const widthOk = !strict || !exactUrl || slot.shownUrl === exactUrl;
      if (slot.shownTime === t && slot.shownProduct === pl.productId && slot.layer.getVisible() && widthOk) continue;
      const url = exactUrl ?? pl.urls[index];
      if (url && this.cache.status(url) === 'error' && !slot.layer.getVisible()) continue;
      return false;
    }
    return true;
  }

  /** Resolve quando o quadro atual estiver na tela (ver `isCurrentFrameReady`). */
  whenCurrentFrameReady(): Promise<void> {
    if (this.isCurrentFrameReady()) return Promise.resolve();
    return new Promise((resolve) => {
      const done = () => {
        this.waiters.delete(done);
        resolve();
      };
      this.waiters.add(done);
    });
  }

  dispose(): void {
    this.disposed = true;
    this.unsubs.forEach((u) => u());
    useTimeStore.getState().setDisplayedTime(null);
    for (const slot of this.slots.values()) {
      this.map.removeLayer(slot.layer);
      this.map.removeLayer(slot.detalhe);
      slot.detalhe.dispose();
      if (slot.shownUrl) this.cache.unpin(slot.shownUrl);
      slot.source.setFrameImage(null);
    }
    this.slots.clear();
    for (const w of [...this.waiters]) w();
  }
}
